// src/AppRoutes.jsx — every route in the app, in one table.
import React, { Suspense, lazy } from 'react';
import { Route, Routes } from 'react-router-dom';
import App from './App';
import FileBrowser from './components/FileBrowser';
import Spinner from './components/Spinner.mjs';
import AdminRoute from './components/AdminRoute';
import NotFound from './components/NotFound';

// `/` (the file browser) is the landing route for virtually every session, so
// it stays eagerly imported. The secondary pages are split out: each one pulls
// in machinery most visits never touch (waveform editing, the tag graph, the
// storage charts, the admin console), and bundling them into the first paint
// made every user pay for all of it.
const StorageDashboardPage = lazy(() => import('./pages/StorageDashboardPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const TagManagerPage = lazy(() => import('./pages/TagManagerPage'));
const ProjectsPage = lazy(() => import('./pages/ProjectsPage'));
const SplitPage = lazy(() => import('./pages/SplitPage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const DesktopAuthPage = lazy(() => import('./pages/DesktopAuthPage'));
const InviteAcceptPage = lazy(() => import('./pages/InviteAcceptPage'));

// Full-viewport centred spinner for the routes OUTSIDE the shell — a chunk
// resolves in a few hundred ms on a cold cache, and a bare white page in that
// window reads as a broken app. (Routes inside the shell suspend within it; see
// AppShell.) Spinner already carries role="status" aria-label="Loading".
const RouteFallback = (
    <div className="route-loading">
        <Spinner />
    </div>
);

/** The library, with the page heading every other page gets from PageHeader. */
const LibraryPage = () => (
    <>
        <h1 className="sr-only">Library</h1>
        <FileBrowser />
    </>
);

export default function AppRoutes() {
    return (
        <Suspense fallback={RouteFallback}>
            <Routes>
                {/* Outside the shell, on purpose. The desktop consent screen is a
                    focused, single-decision flow, and the invitee may not have an
                    account yet — both sign the visitor in themselves. */}
                <Route path="/desktop-auth" element={<DesktopAuthPage />} />
                <Route path="/invite/:inviteId" element={<InviteAcceptPage />} />

                {/* Everything else: signed in, inside the app shell. */}
                <Route element={<App />}>
                    <Route index element={<LibraryPage />} />
                    <Route path="projects" element={<ProjectsPage />} />
                    <Route path="tags" element={<TagManagerPage />} />
                    <Route path="settings" element={<SettingsPage />} />
                    <Route path="workspace/storage" element={<StorageDashboardPage />} />
                    <Route path="split/:fileId" element={<SplitPage />} />
                    <Route path="admin" element={<AdminRoute><AdminPage /></AdminRoute>} />
                    <Route path="*" element={<NotFound />} />
                </Route>
            </Routes>
        </Suspense>
    );
}
